/**
 * SCT Detail Modal Component
 * Task 25.12 - Redesigned with Option C - Side-by-side tree + detail view
 * Task 25.13 - Refactored to use cached hierarchy data instead of API calls
 * 
 * Enhanced modal component for displaying detailed SCT hierarchy information
 * Supports all hierarchy levels: Category, Sub-Category, Similarity Group, and Descriptor
 * Features inline navigation without closing the modal - all client-side, no API calls
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Layers, 
  FolderOpen, 
  FileText
} from 'lucide-react';
import { useLanguage } from '../../contexts';
import { useDatasetVersion } from '../../hooks';
import { trpc } from '../../api';
import { guideCodesEqual } from '../../utils';

// SCT hierarchy node from getGuideHierarchy with includeDescriptions=true
export interface SCTHierarchyNode {
  id?: string;
  code: string;
  name: string;
  nameEn?: string;
  nameFr?: string;
  structureType: string;
  category: string;
  level: number;
  hasValue: boolean;
  entryCount: number;
  parentCode?: string | null;
  descriptionEn?: string | null;
  descriptionFr?: string | null;
  children?: SCTHierarchyNode[];
}

interface SCTDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  sctItemId: string;
  hierarchyLevel: 'category' | 'subCategory' | 'similarityGroup' | 'descriptor';
  hierarchyData?: SCTHierarchyNode[]; // Cached hierarchy data from parent
  onNavigateToLevel?: (itemId: string, level: string) => void;
}

// Breadcrumb interface
interface BreadcrumbItem {
  code: string;
  name: string;
  structureType: string;
}

interface GuideEntryFallback {
  id: string;
  code: string;
  structureType: string;
  category: string;
  nameEn: string;
  nameFr: string;
  descriptionEn: string | null;
  descriptionFr: string | null;
  parentCode: string | null;
  hasValue: boolean;
  level: number;
}

const getStructureTypeIcon = (structureType: string) => {
  switch (structureType) {
    case 'Category': return Layers;
    case 'Sub-Category': return FolderOpen;
    case 'Similarity Group': return FolderOpen;
    case 'Descriptor': return FileText;
    default: return FileText;
  }
};

const getHierarchyLevelFromStructureType = (structureType: string): 'category' | 'subCategory' | 'similarityGroup' | 'descriptor' => {
  const map: Record<string, 'category' | 'subCategory' | 'similarityGroup' | 'descriptor'> = {
    'Category': 'category',
    'Sub-Category': 'subCategory',
    'Similarity Group': 'similarityGroup',
    'Descriptor': 'descriptor',
  };
  return map[structureType] || 'descriptor';
};

/**
 * Find a node in the hierarchy by code
 */
function findNodeByCode(nodes: SCTHierarchyNode[], code: string): SCTHierarchyNode | null {
  for (const node of nodes) {
    if (guideCodesEqual(node.code, code)) return node;
    if (node.children) {
      const found = findNodeByCode(node.children, code);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Build breadcrumbs path to a node
 */
function buildBreadcrumbs(nodes: SCTHierarchyNode[], targetCode: string, path: BreadcrumbItem[] = []): BreadcrumbItem[] | null {
  for (const node of nodes) {
    const currentPath = [...path, { code: node.code, name: node.nameEn || node.name, structureType: node.structureType }];
    if (guideCodesEqual(node.code, targetCode)) {
      return currentPath;
    }
    if (node.children) {
      const found = buildBreadcrumbs(node.children, targetCode, currentPath);
      if (found) return found;
    }
  }
  return null;
}

function mapGuideEntryToHierarchyNode(entry: GuideEntryFallback): SCTHierarchyNode {
  return {
    id: entry.id,
    code: entry.code,
    name: entry.nameEn,
    nameEn: entry.nameEn,
    nameFr: entry.nameFr,
    structureType: entry.structureType,
    category: entry.category,
    level: entry.level,
    hasValue: entry.hasValue,
    entryCount: 0,
    parentCode: entry.parentCode,
    descriptionEn: entry.descriptionEn,
    descriptionFr: entry.descriptionFr,
  };
}

/**
 * SCT Detail Modal Component
 * 
 * Redesigned with Option C: Side-by-side tree + detail view
 * Uses cached hierarchy data for instant navigation without API calls
 */
export const SCTDetailModal: React.FC<SCTDetailModalProps> = React.memo(({ 
  isOpen, 
  onClose, 
  sctItemId: initialItemId, 
  hierarchyLevel: initialHierarchyLevel,
  hierarchyData = [],
  onNavigateToLevel
}) => {
  const { language } = useLanguage();
  const { version, hasResolvedVersion } = useDatasetVersion();
  
  // Internal navigation state - allows browsing without closing modal
  const [currentItemId, setCurrentItemId] = useState(initialItemId);
  const [currentHierarchyLevel, setCurrentHierarchyLevel] = useState(initialHierarchyLevel);
  
  // Resizable nav pane state
  const [navWidth, setNavWidth] = useState(220);
  const [isResizing, setIsResizing] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const MIN_NAV_WIDTH = 160;
  const MAX_NAV_WIDTH = 340;

  // Reset internal state when props change (modal reopened with new item)
  useEffect(() => {
    setCurrentItemId(initialItemId);
    setCurrentHierarchyLevel(initialHierarchyLevel);
  }, [initialItemId, initialHierarchyLevel]);

  // Find current node and related data from cached hierarchy - no API calls!
  const { currentNode, breadcrumbs, children, parent } = useMemo(() => {
    if (!hierarchyData.length || !currentItemId) {
      return { currentNode: null, breadcrumbs: [], children: [] };
    }
    
    const node = findNodeByCode(hierarchyData, currentItemId);
    const crumbs = buildBreadcrumbs(hierarchyData, currentItemId) || [];
    
    return {
      currentNode: node,
      breadcrumbs: crumbs,
      children: node?.children || [],
    };
  }, [hierarchyData, currentItemId]);

  const fallbackEntryQuery = trpc.sct.getGuideEntry.useQuery(
    {
      code: currentItemId,
      language,
      version,
      includeChildren: true,
    },
    {
      enabled:
        isOpen &&
        hasResolvedVersion &&
        Boolean(version) &&
        Boolean(currentItemId) &&
        !currentNode,
      staleTime: 1000 * 60 * 10,
      retry: 1,
    }
  );

  const effectiveCurrentNode = currentNode ?? (
    fallbackEntryQuery.data?.entry ? mapGuideEntryToHierarchyNode(fallbackEntryQuery.data.entry) : null
  );
  const effectiveBreadcrumbs = currentNode
    ? breadcrumbs
    : (fallbackEntryQuery.data?.breadcrumbs ?? []);
  const effectiveChildren = currentNode
    ? children
    : (fallbackEntryQuery.data?.children?.map(mapGuideEntryToHierarchyNode) ?? []);

  // Handle navigation to different items - instant, no API call
  const handleNavigate = useCallback((code: string, structureType: string) => {
    setCurrentItemId(code);
    const nextLevel = getHierarchyLevelFromStructureType(structureType);
    setCurrentHierarchyLevel(nextLevel);
    onNavigateToLevel?.(code, structureType);
  }, [onNavigateToLevel]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Handle nav pane resize
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!navRef.current) return;
      const modalRect = navRef.current.parentElement?.getBoundingClientRect();
      if (!modalRect) return;
      
      const newWidth = e.clientX - modalRect.left;
      setNavWidth(Math.min(MAX_NAV_WIDTH, Math.max(MIN_NAV_WIDTH, newWidth)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!isOpen) return null;

  const structureType = effectiveCurrentNode?.structureType || 'Descriptor';
  const TypeIcon = getStructureTypeIcon(structureType);
  const displayName = language === 'en'
    ? (effectiveCurrentNode?.nameEn || effectiveCurrentNode?.name)
    : (effectiveCurrentNode?.nameFr || effectiveCurrentNode?.name);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 w-full max-w-[920px] h-[64vh] overflow-hidden flex shadow-2xl mx-4 ${isResizing ? 'select-none' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Left: Tree navigation */}
        <div 
          ref={navRef}
          style={{ width: navWidth }}
          className="hidden md:flex border-r border-gray-200 dark:border-slate-700 flex-col bg-gray-50 dark:bg-slate-950 relative shrink-0"
        >
          <div className="px-2.5 py-2 border-b border-gray-200 dark:border-slate-700">
            <span className="text-gray-600 dark:text-slate-400 text-xs uppercase tracking-wider font-medium">Navigation</span>
          </div>
          
          {/* Resize handle */}
          <div
            onMouseDown={handleResizeStart}
            className={`absolute top-0 right-0 w-1 h-full cursor-col-resize group hover:bg-blue-500/50 transition-colors ${isResizing ? 'bg-blue-500' : ''}`}
          >
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-300 dark:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            <div className="space-y-0.5">
              {/* Breadcrumb path as tree items */}
              {effectiveBreadcrumbs.map((crumb, i) => {
                const Icon = getStructureTypeIcon(crumb.structureType);
                const isActive = crumb.code === currentItemId;
                return (
                  <button
                    key={`${crumb.code}-${i}`}
                    onClick={() => handleNavigate(crumb.code, crumb.structureType)}
                    style={{ paddingLeft: `${i * 12 + 8}px` }}
                    className={`w-full flex items-center gap-1.5 py-1.5 pr-2 rounded-md text-left text-xs transition-colors ${
                      isActive 
                        ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400' 
                        : 'text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                    }`}
                    title={crumb.name}
                  >
                    <Icon className="w-4 h-4 shrink-0 text-gray-600 dark:text-slate-300" />
                    <span className="truncate">{crumb.name}</span>
                  </button>
                );
              })}
              
              {/* Children of current node */}
              {effectiveChildren && effectiveChildren.length > 0 && (
                <>
                  {effectiveChildren.map((child) => {
                    const Icon = getStructureTypeIcon(child.structureType);
                    const isChildActive = child.code === currentItemId;
                    const childName = language === 'en' ? (child.nameEn || child.name) : (child.nameFr || child.name);
                    return (
                      <button
                        key={child.code}
                        onClick={() => handleNavigate(child.code, child.structureType)}
                        style={{ paddingLeft: `${effectiveBreadcrumbs.length * 12 + 8}px` }}
                        className={`w-full flex items-center gap-1.5 py-1.5 pr-2 rounded-md text-left text-xs transition-colors ${
                          isChildActive
                            ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                            : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                        }`}
                        title={childName}
                      >
                        <Icon className="w-4 h-4 shrink-0 text-gray-600 dark:text-slate-300" />
                        <span className="truncate">{childName}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Right: Detail view */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="p-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between gap-2">
            <div className="flex h-8 items-stretch gap-2.5 min-w-0">
              <div className="w-8 h-full rounded-md flex items-center justify-center bg-blue-100 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 shrink-0">
                <TypeIcon className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0 flex h-full items-stretch overflow-hidden">
                <h2 className="flex h-full m-0 items-center text-gray-900 dark:text-white font-semibold text-sm leading-none truncate">
                  {displayName || 'Loading...'}
                </h2>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-1 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors shrink-0"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {((!hierarchyData.length && !fallbackEntryQuery.data) || fallbackEntryQuery.isLoading) ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-gray-300 dark:border-slate-600 border-t-blue-500 rounded-full" />
              </div>
            ) : !effectiveCurrentNode ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-slate-500 italic">Item not found in hierarchy</p>
              </div>
            ) : (
              <SCTDetailContent 
                entry={effectiveCurrentNode}
                breadcrumbs={effectiveBreadcrumbs}
                onNavigate={handleNavigate}
                language={language}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
});

// SCT detail content component
interface SCTDetailContentProps {
  entry: SCTHierarchyNode;
  breadcrumbs: BreadcrumbItem[];
  onNavigate: (code: string, structureType: string) => void;
  language: string;
}

const SCTDetailContent: React.FC<SCTDetailContentProps> = ({ 
  entry, 
  breadcrumbs,
  onNavigate,
  language
}) => {
  const description = language === 'en' ? entry.descriptionEn : entry.descriptionFr;
  const displayName = language === 'en' ? (entry.nameEn || entry.name) : (entry.nameFr || entry.name);
  
  return (
    <div className="space-y-3.5">
      {/* Metadata moved from header to keep header row perfectly aligned */}
      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400">
        <span className="inline-flex h-5 items-center gap-0.5 rounded bg-gray-100 px-1.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          <span className="text-gray-500 dark:text-gray-400">#</span>
          <span className="font-mono leading-none">{entry.code}</span>
        </span>
        <span className="inline-flex h-5 items-center">{entry.structureType}</span>
      </div>

      {description ? (
        <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
          {description}
        </p>
      ) : (
        <p className="text-xs text-gray-500 dark:text-slate-500 italic">No description available</p>
      )}

    </div>
  );
};
